<template>
  <v-dialog :value="value" max-width="1100" @input="$emit('input', $event)">
    <v-card>
      <v-toolbar color="primary" dark flat dense>
        <v-toolbar-title>{{
          $t("reports.aiCredit.creditsHistory")
        }}</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <v-card-text class="pt-5">
        <h3 class="black--text mb-1">
          {{ $t("reports.aiCredit.creditsHistory") }}
        </h3>
        <p class="grey--text mb-4">{{ $t("reports.aiCredit.historyDesc") }}</p>

        <!-- Summary cards -->
        <v-row class="mb-2">
          <v-col cols="12" md="4">
            <v-card outlined class="pa-4">
              <div class="black--text font-weight-bold mb-1">
                {{ $t("reports.aiCredit.colCooperative") }}
              </div>
              <div class="text-h5 font-weight-bold black--text">
                {{ coopName || "-" }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="4">
            <v-card outlined class="pa-4 cs-avail">
              <div class="black--text font-weight-bold mb-1">
                {{ $t("reports.aiCredit.creditsAvailable") }}
              </div>
              <div class="d-flex align-baseline flex-wrap" style="gap: 10px">
                <div class="text-h5 font-weight-bold black--text">
                  {{ formatNum(available) }}
                </div>
                <span class="grey--text text-caption" v-if="refreshesOn">
                  {{ $t("reports.aiCredit.refreshesOn") }}:
                  {{ fmtDate(refreshesOn) }}
                </span>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-divider class="my-3" />

        <!-- Search + pagination -->
        <div class="d-flex flex-wrap align-center justify-space-between mb-2">
          <v-text-field
            v-model="search"
            :placeholder="$t('reports.aiCredit.search')"
            prepend-inner-icon="mdi-magnify"
            outlined
            dense
            hide-details
            clearable
            style="max-width: 340px"
          />
          <div class="d-flex align-center">
            <span class="grey--text mr-2">{{ rangeLabel }}</span>
            <v-btn
              icon
              outlined
              color="primary"
              class="mr-1"
              :disabled="page <= 1"
              @click="prevPage"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn
              icon
              outlined
              color="primary"
              :disabled="!hasNextPage"
              @click="nextPage"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- History table -->
        <v-data-table
          :headers="headers"
          :items="filteredMonths"
          :loading="loading"
          hide-default-footer
          class="elevation-0 history-table"
          :no-data-text="$t('reports.aiCredit.noHistory')"
        >
          <template v-slot:[`item.month`]="{ item }">{{
            formatMonth(item.month)
          }}</template>
          <template v-slot:[`item.allocation`]="{ item }">
            <span class="green--text text--darken-1"
              >+{{ formatNum(item.allocation) }}
              {{ $t("reports.aiCredit.credits") }}</span
            >
          </template>
          <template v-slot:[`item.usage`]="{ item }">
            <span class="red--text"
              >-{{ formatNum(item.usage) }}
              {{ $t("reports.aiCredit.credits") }}</span
            >
          </template>
          <template v-slot:[`item.closing`]="{ item }"
            >{{ formatNum(item.closing) }}
            {{ $t("reports.aiCredit.credits") }}</template
          >
        </v-data-table>
      </v-card-text>

      <v-divider />
      <v-card-actions class="py-3 px-4">
        <v-spacer />
        <v-btn color="primary" depressed @click="close">{{
          $t("reports.aiCredit.close")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import AiCreditService from "@/_services/AiCreditService";
import creditFormat from "@/mixins/creditFormat";

export default {
  name: "CreditsHistoryDialog",
  mixins: [creditFormat],
  props: {
    value: { type: Boolean, default: false },
    cooperative: { type: Object, default: null },
  },
  data() {
    return {
      available: 0,
      refreshesOn: null,
      months: [],
      total: 0,
      page: 1,
      limit: 12,
      loading: false,
      search: "",
    };
  },
  computed: {
    coopName() {
      return this.cooperative ? this.cooperative.cooperative : "";
    },
    headers() {
      const t = (k) => this.$t(`reports.aiCredit.${k}`);
      return [
        { text: t("colMonth"), value: "month", sortable: false },
        { text: t("colCreditsAssigned"), value: "allocation", sortable: false },
        { text: t("colCreditsUsed"), value: "usage", sortable: false },
        { text: t("colClosingBalance"), value: "closing", sortable: false },
      ];
    },
    filteredMonths() {
      if (!this.search) return this.months;
      const q = this.search.toLowerCase();
      return this.months.filter((m) =>
        this.formatMonth(m.month).toLowerCase().includes(q)
      );
    },
    hasNextPage() {
      return this.page * this.limit < this.total;
    },
    rangeLabel() {
      const shown = Math.min(this.page * this.limit, this.total);
      return `${shown} ${this.$t("of")} ${this.total}`;
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.page = 1;
        this.search = "";
        this.available = this.cooperative
          ? this.cooperative.availableCredits
          : 0;
        this.refreshesOn = null;
        this.months = [];
        this.fetch();
      }
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    async fetch() {
      if (!this.cooperative) return;
      this.loading = true;
      try {
        const env = await AiCreditService.getHistory(
          this.cooperative.organization,
          {
            page: this.page,
            limit: this.limit,
          }
        );
        const payload = (env && env.data) || {};
        this.available = payload.available || 0;
        this.refreshesOn = payload.refreshesOn || null;
        this.months = payload.months || [];
        this.total = (payload.pagination && payload.pagination.total) || 0;
      } catch (e) {
        this.$notify({
          title: this.$t("reports.aiCredit.errorTitle"),
          text: this.$t("reports.aiCredit.loadFailed"),
          type: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1;
        this.fetch();
      }
    },
    nextPage() {
      if (this.hasNextPage) {
        this.page += 1;
        this.fetch();
      }
    },
    formatMonth(ym) {
      if (!ym) return "-";
      const [y, m] = ym.split("-");
      const names = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${names[Number(m) - 1] || m} ${y}`;
    },
  },
};
</script>

<style scoped>
.cs-avail {
  background: #f1faf4;
  border-color: #b7e3c9 !important;
}
.history-table >>> thead th {
  background: #1b7a43;
  color: #fff !important;
  font-weight: 600;
}
</style>
